import React, { useState, useEffect } from 'react';
import { Phone, Clock, Plus, Send, CheckCircle, AlertCircle, XCircle, Calendar, User, HelpCircle, FileText, Sparkles, Filter, RefreshCw, X, CornerDownRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import { useAuth } from '@/App';

const CallRequestsParent = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'pending' | 'accepted' | 'postponed' | 'rejected' | 'done'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    student_id: '',
    student_name: '',
    request_type: 'Call Request',
    reason: '',
    what_to_happen: 'Warden Call-back',
    when_date: '',
    who_person: '',
    priority: 'Normal'
  });

  useEffect(() => {
    fetchChildren();
    fetchRequests();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await apiClient.get('/v1/parent/my-children');
      const kids = response.data.children || response.data || [];
      setChildren(kids);
      if (kids.length > 0) {
        setFormData(prev => ({
          ...prev,
          student_id: prev.student_id || kids[0].id,
          student_name: prev.student_name || kids[0].name,
          who_person: prev.who_person || `${user?.name || 'Parent'} (${user?.phone || 'N/A'})`
        }));
      }
    } catch (err) {
      console.error('Failed to load children:', err);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/v1/parent/notifications/call-history');
      const list = response.data.calls || response.data || [];
      setRequests(list);
    } catch (err) {
      toast.error('Failed to load your requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!formData.reason.trim()) {
      toast.error('Please enter a request description');
      return;
    }

    setSubmitting(true);
    try {
      const selectedKid = children.find(c => c.id === formData.student_id) || children[0];
      const payload = {
        ...formData,
        student_id: formData.student_id || selectedKid?.id || '',
        student_name: selectedKid?.name || formData.student_name || 'Child',
        who_person: formData.who_person || `${user?.name || 'Parent'} (${user?.phone || ''})`
      };

      const res = await apiClient.post('/v1/parent/notifications/submit-request', payload);
      if (res.data.success) {
        toast.success('Request submitted successfully!');
        setShowCreateModal(false);
        setFormData({
          student_id: children[0]?.id || '',
          student_name: children[0]?.name || '',
          request_type: 'Call Request',
          reason: '',
          what_to_happen: 'Warden Call-back',
          when_date: '',
          who_person: `${user?.name || 'Parent'} (${user?.phone || 'N/A'})`,
          priority: 'Normal'
        });
        fetchRequests();
      } else {
        toast.error(res.data.message || 'Failed to submit request');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error submitting request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      const res = await apiClient.post(`/v1/parent/notifications/cancel-request/${requestId}`);
      if (res.data.success) {
        toast.success('Request cancelled');
        fetchRequests();
      } else {
        toast.error(res.data.message || 'Could not cancel request');
      }
    } catch (err) {
      toast.error('Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white gap-1"><Clock className="w-3 h-3" /> Pending Review</Badge>;
      case 'accepted':
      case 'approved':
        return <Badge className="bg-emerald-600 text-white gap-1"><CheckCircle className="w-3 h-3" /> Accepted</Badge>;
      case 'postponed':
        return <Badge className="bg-blue-600 text-white gap-1"><Clock className="w-3 h-3" /> Postponed</Badge>;
      case 'rejected':
        return <Badge className="bg-rose-600 text-white gap-1"><XCircle className="w-3 h-3" /> Rejected</Badge>;
      case 'done':
      case 'completed':
        return <Badge className="bg-slate-700 text-white gap-1"><CheckCircle className="w-3 h-3" /> Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-400 text-white gap-1"><XCircle className="w-3 h-3" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredRequests = requests.filter(r => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return r.status === 'pending';
    if (activeTab === 'accepted') return r.status === 'accepted' || r.status === 'approved';
    if (activeTab === 'postponed') return r.status === 'postponed';
    if (activeTab === 'rejected') return r.status === 'rejected';
    if (activeTab === 'done') return r.status === 'done' || r.status === 'completed';
    return true;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted' || r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;
  const postponedCount = requests.filter(r => r.status === 'postponed').length;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Phone className="w-8 h-8 text-blue-600" />
            Parent Requests & Calls
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Submit call requests, leave applications, and inquiries to warden & management
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchRequests} variant="outline" size="sm" className="gap-1.5">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2 shadow-md">
            <Plus className="w-4 h-4" /> New Request
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-100/80 p-1.5 rounded-xl text-xs md:text-sm font-medium">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-3.5 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
            activeTab === 'all' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <FileText className="w-3.5 h-3.5" /> All ({requests.length})
        </button>

        <button
          onClick={() => setActiveTab('pending')}
          className={`px-3.5 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
            activeTab === 'pending' ? 'bg-white text-amber-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Clock className="w-3.5 h-3.5 text-amber-500" /> Pending ({pendingCount})
        </button>

        <button
          onClick={() => setActiveTab('accepted')}
          className={`px-3.5 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
            activeTab === 'accepted' ? 'bg-white text-emerald-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Accepted ({acceptedCount})
        </button>

        <button
          onClick={() => setActiveTab('postponed')}
          className={`px-3.5 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
            activeTab === 'postponed' ? 'bg-white text-blue-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Clock className="w-3.5 h-3.5 text-blue-600" /> Postponed ({postponedCount})
        </button>

        <button
          onClick={() => setActiveTab('rejected')}
          className={`px-3.5 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
            activeTab === 'rejected' ? 'bg-white text-rose-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <XCircle className="w-3.5 h-3.5 text-rose-600" /> Rejected ({rejectedCount})
        </button>

        <button
          onClick={() => setActiveTab('done')}
          className={`px-3.5 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
            activeTab === 'done' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <CheckCircle className="w-3.5 h-3.5 text-slate-600" /> Done
        </button>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading your requests...</div>
        ) : filteredRequests.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <HelpCircle className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-600 font-semibold">No requests found in this view</p>
            <p className="text-slate-400 text-xs mt-1">Click "+ New Request" to submit a new request to the warden.</p>
          </Card>
        ) : (
          filteredRequests.map((req) => (
            <Card key={req.id} className="border border-slate-200 hover:shadow-md transition-all overflow-hidden">
              <CardContent className="p-6 space-y-4">
                {/* Header row: Badge, Type, Status */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">{req.request_type || 'Request'}</Badge>
                    {req.priority && (
                      <Badge variant={req.priority === 'Urgent' ? 'destructive' : 'outline'}>
                        {req.priority} Priority
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(req.status)}
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(req.created_at || Date.now()).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Main Description */}
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Request Description</h4>
                  <p className="text-slate-800 text-sm font-medium leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                    {req.reason}
                  </p>
                </div>

                {/* Additional Details Grid: What to happen, When, Who */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-blue-50/40 p-4 rounded-xl border border-blue-100 text-xs">
                  <div>
                    <span className="font-bold text-blue-900 flex items-center gap-1 mb-1">
                      🎯 What to Happen:
                    </span>
                    <p className="text-slate-700 font-medium">{req.what_to_happen || req.reason || 'Warden Call-back'}</p>
                  </div>

                  <div>
                    <span className="font-bold text-blue-900 flex items-center gap-1 mb-1">
                      📅 When:
                    </span>
                    <p className="text-slate-700 font-medium">{req.when_date || req.preferred_time || 'As soon as possible'}</p>
                  </div>

                  <div>
                    <span className="font-bold text-blue-900 flex items-center gap-1 mb-1">
                      👤 Who (Child & Guardian):
                    </span>
                    <p className="text-slate-700 font-medium">Child: {req.student_name || 'Student'}</p>
                    <p className="text-slate-500 text-[11px]">{req.who_person || req.parent_name}</p>
                  </div>
                </div>

                {/* Prominent Rejection Banner if Rejected */}
                {(req.status === 'rejected' || req.rejection_reason) && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 text-xs md:text-sm text-rose-900 space-y-1.5">
                    <div className="flex items-center gap-2 font-bold text-rose-800">
                      <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                      <span>Request Rejected</span>
                      {req.updated_by_name && (
                        <span className="text-xs font-normal text-rose-600">
                          by {req.updated_by_name} ({req.updated_by_role || 'Warden'})
                        </span>
                      )}
                    </div>
                    <div className="pl-6 text-rose-800">
                      <span className="font-semibold text-rose-900">Rejection Reason: </span>
                      <span>
                        {req.rejection_reason || (req.warden_note ? req.warden_note.replace(/^Rejected:\s*/i, '') : 'No specific reason provided.')}
                      </span>
                    </div>
                  </div>
                )}

                {/* Prominent Postponed Banner if Postponed */}
                {(req.status === 'postponed' || req.postponed_reason) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-xs md:text-sm text-blue-900 space-y-1.5">
                    <div className="flex items-center gap-2 font-bold text-blue-800">
                      <Clock className="w-4 h-4 text-blue-600 shrink-0" />
                      <span>Request Postponed / Rescheduled</span>
                      {req.updated_by_name && (
                        <span className="text-xs font-normal text-blue-600">
                          by {req.updated_by_name} ({req.updated_by_role || 'Warden'})
                        </span>
                      )}
                    </div>
                    <div className="pl-6 text-blue-800">
                      <span className="font-semibold text-blue-900">Reschedule Note: </span>
                      <span>
                        {req.postponed_reason || (req.warden_note ? req.warden_note.replace(/^Postponed:\s*/i, '') : 'Details pending.')}
                      </span>
                    </div>
                  </div>
                )}

                {/* Prominent Accepted Banner if Accepted */}
                {req.status === 'accepted' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs md:text-sm text-emerald-900 flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>
                      <strong>Request Accepted:</strong> The warden has accepted your request and will connect with you.
                      {req.updated_by_name && <span className="text-emerald-700 ml-1">({req.updated_by_name})</span>}
                    </span>
                  </div>
                )}

                {/* Footer Action for Pending Requests */}
                {req.status === 'pending' && (
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={() => handleCancelRequest(req.id)}
                      variant="ghost"
                      size="sm"
                      className="text-rose-600 hover:bg-rose-50 text-xs gap-1"
                    >
                      <XCircle className="w-4 h-4" /> Cancel Request
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create New Request Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Submit New Parent Request
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateRequest} className="space-y-4 pt-2">
            {/* Child Selection */}
            {children.length > 0 && (
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Select Child</label>
                <select
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  value={formData.student_id}
                  onChange={(e) => {
                    const kid = children.find(c => c.id === e.target.value);
                    setFormData({
                      ...formData,
                      student_id: e.target.value,
                      student_name: kid?.name || ''
                    });
                  }}
                >
                  {children.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.class_name || 'Student'})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Request Type */}
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Request Category</label>
              <select
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                value={formData.request_type}
                onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
              >
                <option value="Call Request">Call Request (Warden & Child)</option>
                <option value="Leave & Outpass">Leave & Outpass Application</option>
                <option value="General Inquiry">General Inquiry / Discussion</option>
                <option value="Special Instruction">Special Care / Food Instruction</option>
              </select>
            </div>

            {/* What to happen */}
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">🎯 What to Happen (Action Requested)</label>
              <input
                type="text"
                placeholder="e.g., Scheduled Call-back, Outpass Gate Pass, Medical Update"
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                value={formData.what_to_happen}
                onChange={(e) => setFormData({ ...formData, what_to_happen: e.target.value })}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Request Description *</label>
              <textarea
                rows={3}
                required
                placeholder="Describe your request in detail (what you need, reasons, or instructions)..."
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            </div>

            {/* When (Date & Time) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">📅 When (Date & Preferred Time)</label>
                <input
                  type="text"
                  placeholder="e.g., Tomorrow at 6:00 PM"
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  value={formData.when_date}
                  onChange={(e) => setFormData({ ...formData, when_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Priority</label>
                <select
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Who Person */}
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">👤 Who (Contact Guardian Details)</label>
              <input
                type="text"
                placeholder="Guardian Name & Phone Number"
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                value={formData.who_person}
                onChange={(e) => setFormData({ ...formData, who_person: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallRequestsParent;
